/**
 * Label / QR System — Generate printable labels with QR codes for each part
 */
import { useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tag, Download, Printer } from 'lucide-react';

// Simple QR code generator using SVG (no external dependency)
function QRCodeSVG({ data, size = 60 }) {
  // Simple visual hash — not a real QR but a unique pattern per part code
  const hash = Array.from(data).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
  const bits = Array.from({ length: 49 }, (_, i) => ((hash * (i + 7)) >> (i % 16)) & 1);
  const gridSize = 7;
  const cellSize = size / (gridSize + 2);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <rect width={size} height={size} fill="white" />
      {/* Position markers */}
      {[[0,0],[0,5],[5,0]].map(([r,c], i) => (
        <g key={`m${i}`}>
          <rect x={(c + 0.5) * cellSize} y={(r + 0.5) * cellSize} width={cellSize * 2} height={cellSize * 2} fill="black" />
          <rect x={(c + 0.75) * cellSize} y={(r + 0.75) * cellSize} width={cellSize * 1.5} height={cellSize * 1.5} fill="white" />
          <rect x={(c + 1) * cellSize} y={(r + 1) * cellSize} width={cellSize} height={cellSize} fill="black" />
        </g>
      ))}
      {/* Data modules */}
      {bits.map((bit, i) => {
        const row = Math.floor(i / gridSize);
        const col = i % gridSize;
        return bit ? <rect key={i} x={(col + 1) * cellSize} y={(row + 1) * cellSize} width={cellSize * 0.9} height={cellSize * 0.9} fill="black" /> : null;
      })}
    </svg>
  );
}

export default function LabelQRSystem({ cutList = [], estimateName = '', config = {} }) {
  const printRef = useRef(null);
  const projectName = estimateName || 'Wardrobe Project';
  const clientName = config.clientName || 'Client';
  const room = 'Master Bedroom';

  const labels = useMemo(() => cutList.map((part, idx) => ({
    ...part,
    projectName,
    clientName,
    room,
    module: 'Wardrobe',
    thickness: part.material?.includes('18') ? '18mm' : part.material?.includes('12') ? '12mm' : part.material?.includes('8') ? '8mm' : part.material?.includes('16') ? '16mm' : '?',
    finish: part.laminate === 'External' ? 'Premium Laminate' : part.laminate === 'Internal' ? 'Internal Laminate' : 'Raw',
    qrData: `${projectName}|${part.code}|${part.w}x${part.h}|${part.material}`,
  })), [cutList, projectName, clientName]);

  const handlePrint = () => {
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Part Labels - ${projectName}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
        body { padding: 10mm; }
        .label-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
        .label { border: 1px solid #333; padding: 3mm; font-size: 8pt; page-break-inside: avoid; }
        .label h4 { font-size: 10pt; margin-bottom: 2mm; border-bottom: 1px solid #ccc; padding-bottom: 1mm; }
        .label .row { display: flex; justify-content: space-between; margin: 1mm 0; }
        .label .code { font-size: 14pt; font-weight: bold; font-family: monospace; }
        .label .qr { text-align: center; margin-top: 2mm; }
        @media print { @page { size: A4; margin: 10mm; } }
      </style></head><body>
      <div class="label-grid">${printContent}</div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-5" data-testid="label-qr-system">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Tag size={18} className="text-pink-600" /><h3 className="font-bold text-stone-800">Label / QR System</h3>
          <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full">{labels.length} labels</span>
        </div>
        <Button size="sm" variant="outline" onClick={handlePrint} data-testid="print-labels-btn"><Printer size={14} className="mr-1" />Print Labels (A4)</Button>
      </div>

      {/* Label preview grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {labels.map((label, i) => (
          <Card key={i} className="border-stone-300">
            <CardContent className="p-3">
              <div className="flex justify-between items-start mb-1">
                <span className="font-mono font-bold text-lg text-blue-700" data-testid={`label-code-${label.code}`}>{label.code}</span>
                <QRCodeSVG data={label.qrData} size={40} />
              </div>
              <p className="text-xs font-semibold text-stone-900 truncate">{label.part}</p>
              <div className="mt-1.5 space-y-0.5 text-[10px] text-stone-600">
                <div className="flex justify-between"><span>Size</span><span className="font-mono font-bold">{label.w} x {label.h} mm</span></div>
                <div className="flex justify-between"><span>Material</span><span>{label.material}</span></div>
                <div className="flex justify-between"><span>Thickness</span><span>{label.thickness}</span></div>
                <div className="flex justify-between"><span>Finish</span><span>{label.finish}</span></div>
                <div className="flex justify-between"><span>Qty</span><span className="font-bold">{label.qty}</span></div>
              </div>
              <div className="mt-1.5 pt-1.5 border-t border-stone-200 text-[9px] text-stone-400">
                <p>{label.projectName} | {label.room}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hidden print content */}
      <div ref={printRef} className="hidden">
        {labels.map((label, i) => (
          <div key={i} className="label">
            <h4>{label.code} — {label.part}</h4>
            <div className="row"><span>Project:</span><span>{label.projectName}</span></div>
            <div className="row"><span>Room:</span><span>{label.room}</span></div>
            <div className="row"><span>Size:</span><span>{label.w} x {label.h} mm</span></div>
            <div className="row"><span>Material:</span><span>{label.material} ({label.thickness})</span></div>
            <div className="row"><span>Finish:</span><span>{label.finish}</span></div>
            <div className="row"><span>Qty:</span><span>{label.qty}</span></div>
            <div className="code">{label.code}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
